import { useMemo, useRef } from "react";
import { usePrimitivePoints } from "./PrimitivePointsContext";
import { getAnchorCoordinates } from "./PathPopulateContext";
import { roundDeg } from "../../utils/primitivePointsPlacement";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

const PHASE_LABELS = {
    generate: "Preparing primitive...",
    "rotate-project": "Projecting points...",
    assign: "Assigning objects...",
} as const;

export function PrimitivePointsViewer() {
    const {
        objectInnerSvgs,
        placements,
        estimatedPlacementCount,
        placementComputePending,
        placementComputeProgress,
        generatePlacements,
        cancelPlacementCompute,
        canExport,
        handleDownload,
        viewBoxAttr,
        anchor,
        scaleFactor,
        baseRotationDeg,
        depthScaleEnabled,
        depthScaleStrength,
        rotationXDeg,
        rotationYDeg,
        setRotationXDeg,
        setRotationYDeg,
    } = usePrimitivePoints();
    const dragRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        startRotX: number;
        startRotY: number;
    } | null>(null);

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(/\s+/).map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const progressText = useMemo(() => {
        if (!placementComputeProgress) return "Computing...";
        const label = PHASE_LABELS[placementComputeProgress.phase];
        if (placementComputeProgress.total <= 0) return label;
        const pct = Math.round(
            (placementComputeProgress.processed / placementComputeProgress.total) *
                100,
        );
        return `${label} (${Math.max(0, Math.min(100, pct))}%)`;
    }, [placementComputeProgress]);

    const depthRange = useMemo(() => {
        let minDepth = Infinity;
        let maxDepth = -Infinity;
        for (const p of placements) {
            minDepth = Math.min(minDepth, p.depth);
            maxDepth = Math.max(maxDepth, p.depth);
        }
        return { minDepth, maxDepth };
    }, [placements]);

    const painterOrderedPlacements = useMemo(() => {
        return [...placements].sort((a, b) => b.depth - a.depth);
    }, [placements]);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerDown(event); // middle-button pan (button 1)
        if (event.button !== 0) return;
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startRotX: rotationXDeg,
            startRotY: rotationYDeg,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerMove(event); // middle-button pan
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        const sensitivity = 0.45;
        setRotationYDeg(roundDeg(drag.startRotY + dx * sensitivity));
        setRotationXDeg(roundDeg(drag.startRotX + dy * sensitivity));
    };

    const handlePointerEnd = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerUp(event); // middle-button pan release
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            // no-op
        }
    };

    return (
        <div className="panel-viewer path-populate-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerEnd}
                            onPointerCancel={handlePointerEnd}
                            style={{ cursor: "grab", touchAction: "none" }}
                        >
                            {painterOrderedPlacements.map((point, index) => {
                                const objectSvg = objectInnerSvgs[point.objectIndex];
                                if (!objectSvg) return null;
                                const dims = objectSvg.dimensions;
                                const anchorCoords = getAnchorCoordinates(anchor);
                                const offsetX = dims
                                    ? -anchorCoords.x * dims.width * scaleFactor
                                    : 0;
                                const offsetY = dims
                                    ? -anchorCoords.y * dims.height * scaleFactor
                                    : 0;
                                const depthSpan =
                                    depthRange.maxDepth - depthRange.minDepth;
                                const farness =
                                    Number.isFinite(depthSpan) &&
                                    Math.abs(depthSpan) > 1e-9
                                        ? Math.max(
                                              0,
                                              Math.min(
                                                  1,
                                                  (point.depth -
                                                      depthRange.minDepth) /
                                                      depthSpan,
                                              ),
                                          )
                                        : 0;
                                const strength = Math.max(
                                    0,
                                    Math.min(1, depthScaleStrength),
                                );
                                const minFactor = Math.max(
                                    0.1,
                                    1 - strength * 0.8,
                                );
                                const depthScale = depthScaleEnabled
                                    ? 1 - (1 - minFactor) * farness
                                    : 1;
                                const objectScale = scaleFactor * depthScale;
                                const centerX = dims ? dims.width * 0.5 : 0;
                                const centerY = dims ? dims.height * 0.5 : 0;
                                const flipTransform =
                                    point.flipX || point.flipY
                                        ? `translate(${centerX}, ${centerY}) scale(${point.flipX ? -1 : 1}, ${point.flipY ? -1 : 1}) translate(${-centerX}, ${-centerY})`
                                        : undefined;
                                return (
                                    <g
                                        key={index}
                                        transform={`translate(${point.x + offsetX}, ${point.y + offsetY}) rotate(${roundDeg(baseRotationDeg + point.rotationDeg)}, ${-offsetX}, ${-offsetY}) scale(${objectScale})`}
                                    >
                                        <g
                                            transform={flipTransform}
                                            dangerouslySetInnerHTML={{
                                                __html: objectSvg.content,
                                            }}
                                        />
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {placementComputePending
                            ? progressText
                            : `${placements.length} rendered - est. ${estimatedPlacementCount} points`}
                    </p>
                    <div className="path-tool-footer-actions">
                        {placementComputePending ? (
                            <button
                                type="button"
                                className="generic-button"
                                onClick={cancelPlacementCompute}
                            >
                                Cancel
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="generic-button"
                                disabled={objectInnerSvgs.length === 0}
                                onClick={() => {
                                    void generatePlacements();
                                }}
                            >
                                Generate
                            </button>
                        )}
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!canExport}
                            onClick={handleDownload}
                        >
                            Download result SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
